import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';
import { getCategoryIcon, BUILT_IN_CATEGORIES, getDefaultUnitType } from '@/lib/constants';

interface Product {
  id: string;
  name: string;
  category: string;
  currentStock: number;
  unitType: string;
}

interface StockCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'MORNING' | 'EVENING';
  products: Product[];
  onCountsReady: (counts: Record<string, number>) => void;
  originalPreFills: Record<string, number>;
  initialCounts?: Array<{ productId: string; quantity: number }>;
  isEditMode?: boolean;
  isAdmin?: boolean;
  isDisconnected?: boolean;
  onCreateProduct?: (name: string, category: string, quality?: string) => Promise<Product>;
}

export function StockCountModal({ isOpen, onClose, type, products, onCountsReady, originalPreFills, initialCounts, isEditMode, isAdmin, isDisconnected, onCreateProduct }: StockCountModalProps) {
  const [counts, setCounts] = useState<Record<string, string>>(() => {
    // Pre-fill with initial counts if in edit mode
    if (initialCounts) {
      const initial: Record<string, string> = {};
      initialCounts.forEach(c => {
        initial[c.productId] = c.quantity.toString();
      });
      return initial;
    }
    return {};
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(() => {
    // In disconnected mode, initialize from initialCounts if editing
    if (isDisconnected && initialCounts) {
      return new Set(initialCounts.map(c => c.productId));
    }
    return new Set();
  });
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  // Inline product creation state
  const [customProducts, setCustomProducts] = useState<Product[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductCategory, setNewProductCategory] = useState('FLOWER');
  const [newProductQuality, setNewProductQuality] = useState<string>('PREMIUM');
  const [isCreating, setIsCreating] = useState(false);

  const qualityLevels = [
    { value: 'PREMIUM', label: 'AAA', icon: '💎', color: 'bg-gradient-gold text-dark border-primary shadow-gold' },
    { value: 'HIGH', label: 'Greendoor', icon: '🌿', color: 'bg-green-600 text-white border-green-400 shadow-green-500/30' },
  ];
  const showQuality = newProductCategory === 'FLOWER' || newProductCategory === 'PREROLL';

  if (!isOpen) return null;

  // Combine server products + locally created products
  const allProducts = [...products, ...customProducts];

  // Track products removed by user in normal mode
  const [excludedProductIds, setExcludedProductIds] = useState<Set<string>>(new Set());

  // Derive visible products based on mode
  const visibleProducts = isDisconnected
    ? allProducts.filter(p => selectedProductIds.has(p.id))
    : allProducts.filter(p => !excludedProductIds.has(p.id));

  const filteredProducts = visibleProducts.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Products available to add in picker (not yet selected)
  const availableProducts = allProducts.filter(p =>
    !selectedProductIds.has(p.id) &&
    p.name.toLowerCase().includes(pickerSearch.toLowerCase())
  );

  const handleCountChange = (productId: string, value: string) => {
    // Allow numbers, periods, and commas only
    const normalizedValue = value.replace(',', '.');
    if (normalizedValue === '' || /^\d*\.?\d*$/.test(normalizedValue)) {
      setCounts(prev => ({ ...prev, [productId]: normalizedValue }));
    }
  };

  const handleAddProduct = (productId: string) => {
    setSelectedProductIds(prev => new Set([...prev, productId]));
    haptic.light();
  };

  const handleRemoveProduct = (productId: string) => {
    if (isDisconnected) {
      setSelectedProductIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    } else {
      setExcludedProductIds(prev => new Set([...prev, productId]));
    }
    setCounts(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    haptic.light();
  };

  const handleCreateProduct = async () => {
    if (!onCreateProduct || !newProductName.trim()) return;

    setIsCreating(true);
    try {
      const quality = showQuality ? newProductQuality : undefined;
      const product = await onCreateProduct(newProductName.trim(), newProductCategory, quality);
      setCustomProducts(prev => [...prev, product]);
      setSelectedProductIds(prev => new Set([...prev, product.id]));
      setNewProductName('');
      setNewProductCategory('FLOWER');
      setNewProductQuality('PREMIUM');
      setShowCreateForm(false);
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      setIsCreating(false);
    }
  };

  // Warning detection functions
  const hasWarning = (productId: string, value: string): boolean => {
    if (!value || value === '') return false;

    const quantity = parseFloat(value);
    if (isNaN(quantity)) return false;

    const preFill = originalPreFills[productId];

    // Warn if unchanged from pre-fill or zero
    return (preFill !== undefined && quantity === preFill) || quantity === 0;
  };

  const getWarningMessage = (productId: string, value: string): string => {
    if (!value || value === '') return '';

    const quantity = parseFloat(value);
    if (isNaN(quantity)) return '';

    const preFill = originalPreFills[productId];

    if (quantity === 0) return 'Zero count';
    if (preFill !== undefined && quantity === preFill) return `Unchanged (${quantity}g)`;

    return '';
  };

  const handleSubmit = () => {
    // Convert string values to numbers for submission
    const numericCounts: Record<string, number> = {};

    // In disconnected mode, only submit for visible products
    const productsToSubmit = isDisconnected ? visibleProducts : allProducts;

    Object.entries(counts).forEach(([productId, value]) => {
      // Only include products that are in our submission set
      if (!productsToSubmit.find(p => p.id === productId)) return;
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        numericCounts[productId] = numValue;
      }
    });
    // Call parent handler - parent will decide whether to show confirmation or save
    onCountsReady(numericCounts);
    // Don't clear counts or close modal - parent controls that
  };

  // Count filled products (including pre-filled zeros exception)
  const filledCount = visibleProducts.filter(product => {
    const value = counts[product.id];
    const preFill = originalPreFills[product.id];

    // Count as filled if:
    // 1. Has a value entered, OR
    // 2. Pre-fill is 0 (can stay empty/0)
    if (value !== undefined && value !== '' && !isNaN(parseFloat(value))) {
      return true;
    }

    if (preFill === 0) {
      return true;  // Pre-filled zero counts as filled
    }

    return false;
  }).length;
  const totalProducts = visibleProducts.length;

  // In disconnected mode, require at least 1 product selected and all filled
  const canSubmit = isDisconnected
    ? totalProducts > 0 && filledCount === totalProducts
    : filledCount >= totalProducts;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
      <div className="bg-dark w-full max-h-[90vh] rounded-t-3xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-dark-card border-b border-primary/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-gold">
              {isEditMode ? '✏️ Edit' : ''} {type === 'MORNING' ? '☀️ Morning' : '🌙 Evening'} Stock Count
            </h2>
            <button
              onClick={() => { haptic.light(); onClose(); }}
              className="text-gray-400 hover:text-white text-2xl transition-all active:scale-[0.97]"
            >
              ✕
            </button>
          </div>

          {/* Historical mode banner */}
          {isDisconnected && (
            <div className="mb-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-400">
                Historical entry mode - add products you want to count
              </p>
            </div>
          )}

          {/* Enhanced progress indicator */}
          <div className="text-sm">
            <span className={cn(
              "font-medium",
              totalProducts === 0 ? "text-gray-400" :
              filledCount === totalProducts ? "text-success" : "text-yellow-500"
            )}>
              {totalProducts === 0
                ? 'No products added yet'
                : `${filledCount}/${totalProducts} products entered`}
            </span>
            {totalProducts > 0 && filledCount < totalProducts && (
              <span className="text-gray-400 ml-2">
                ({totalProducts - filledCount} remaining)
              </span>
            )}
          </div>

          {/* Add Product button (disconnected mode) */}
          {isDisconnected && (
            <button
              onClick={() => { haptic.light(); setShowProductPicker(true); setPickerSearch(''); setShowCreateForm(false); }}
              className="mt-3 w-full px-4 py-2.5 bg-primary/10 border-2 border-dashed border-primary/40 rounded-xl text-primary font-medium text-sm hover:bg-primary/20 transition-all active:scale-[0.98]"
            >
              + Add Product
            </button>
          )}

          {/* Search (show when there are products to search) */}
          {((!isDisconnected && allProducts.length > 0) || (isDisconnected && totalProducts > 3)) && (
            <div className="relative mt-3">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                🔍
              </div>
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
        </div>

        {/* Product Picker Overlay */}
        {showProductPicker && (
          <div className="absolute inset-0 bg-dark z-10 flex flex-col">
            <div className="bg-dark-card border-b border-primary/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white">Add Products</h3>
                <button
                  onClick={() => { haptic.light(); setShowProductPicker(false); setShowCreateForm(false); }}
                  className="text-gray-400 hover:text-white text-2xl transition-all active:scale-[0.97]"
                >
                  ✕
                </button>
              </div>
              {!showCreateForm && (
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    🔍
                  </div>
                  <Input
                    placeholder="Search products..."
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Inline Create Form */}
            {showCreateForm ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="bg-dark-card rounded-xl p-4 border-2 border-primary/30">
                  <h4 className="text-sm font-bold text-primary mb-3">New Product</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Product Name</label>
                      <Input
                        value={newProductName}
                        onChange={(e) => setNewProductName(e.target.value)}
                        placeholder="e.g. Blue Dream 3.5g"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Category</label>
                      <select
                        value={newProductCategory}
                        onChange={(e) => setNewProductCategory(e.target.value)}
                        className="w-full px-4 py-3 bg-dark-lighter border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-primary transition-all appearance-none cursor-pointer"
                      >
                        {BUILT_IN_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>
                            {getCategoryIcon(cat)} {cat}
                          </option>
                        ))}
                      </select>
                    </div>
                    {showQuality && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Quality Level</label>
                        <div className="grid grid-cols-2 gap-2">
                          {qualityLevels.map((level) => (
                            <button
                              key={level.value}
                              type="button"
                              onClick={() => { haptic.select(); setNewProductQuality(level.value); }}
                              className={cn(
                                'flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all active:scale-[0.97] border-2',
                                newProductQuality === level.value
                                  ? level.color
                                  : 'bg-dark-lighter text-gray-400 border-gray-700/50 hover:border-primary/50'
                              )}
                            >
                              <span>{level.icon}</span>
                              <span>{level.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => { setShowCreateForm(false); setNewProductName(''); setNewProductQuality('PREMIUM'); }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleCreateProduct}
                        disabled={!newProductName.trim() || isCreating}
                        isLoading={isCreating}
                        className="flex-1"
                      >
                        Create & Add
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {/* Create New Product button */}
                {onCreateProduct && (
                  <button
                    onClick={() => {
                      haptic.light();
                      setShowCreateForm(true);
                    }}
                    className="w-full bg-primary/10 rounded-lg p-3 border-2 border-dashed border-primary/40 hover:bg-primary/20 transition-all active:scale-[0.98] flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">➕</span>
                      <div className="text-left">
                        <div className="font-medium text-primary">Create New Product</div>
                        <div className="text-xs text-gray-400">Add a product not in the system</div>
                      </div>
                    </div>
                  </button>
                )}
                {availableProducts.length === 0 && !onCreateProduct ? (
                  <div className="text-center py-8 text-gray-400">
                    {pickerSearch ? 'No matching products' : 'All products have been added'}
                  </div>
                ) : (
                  availableProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        handleAddProduct(product.id);
                        // Don't close picker - let user add multiple
                      }}
                      className="w-full bg-dark-card rounded-lg p-3 border-2 border-gray-700 hover:border-primary/50 transition-all active:scale-[0.98] flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{getCategoryIcon(product.category)}</span>
                        <div className="text-left">
                          <div className="font-medium text-white">{product.name}</div>
                          <div className="text-xs text-gray-400">{product.category}</div>
                        </div>
                      </div>
                      <span className="text-primary text-sm font-medium">+ Add</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {!showCreateForm && (
              <div className="p-4 bg-dark-card border-t border-primary/20">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => { haptic.light(); setShowProductPicker(false); }}
                  className="w-full"
                >
                  Done ({selectedProductIds.size} selected)
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Product Grid */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {isDisconnected && totalProducts === 0 && (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-gray-400 text-sm">Tap "Add Product" to start building your count</p>
            </div>
          )}
          {filteredProducts.map((product) => {
            const countedStr = counts[product.id];

            return (
              <div
                key={product.id}
                className={cn(
                  'bg-dark-card rounded-lg p-3 border-2 transition-all',
                  countedStr && countedStr !== ''
                    ? 'border-success/50'
                    : 'border-gray-700'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-white">{product.name}</div>
                      <button
                        onClick={() => handleRemoveProduct(product.id)}
                        className="text-gray-500 hover:text-error text-xs transition-colors"
                        title="Remove product"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <input
                      type="text"
                      value={counts[product.id] ?? ''}
                      onChange={(e) => handleCountChange(product.id, e.target.value)}
                      placeholder="0.0"
                      className={cn(
                        "w-20 px-2 py-1 bg-dark-lighter border rounded text-center text-white focus:outline-none transition-colors",
                        hasWarning(product.id, counts[product.id] ?? '')
                          ? "border-yellow-500 bg-yellow-500/10 ring-1 ring-yellow-500/50 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/50"
                          : "border-gray-600 focus:border-primary focus:ring-2 focus:ring-primary/50"
                      )}
                    />
                    {hasWarning(product.id, counts[product.id] ?? '') && (
                      <span className="text-yellow-500 text-xs flex items-center gap-1 whitespace-nowrap">
                        ⚠️ {getWarningMessage(product.id, counts[product.id] ?? '')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        <div className="p-4 bg-dark-card border-t border-primary/20">
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full"
          >
            {isDisconnected && totalProducts === 0 ? (
              'Add products to begin'
            ) : !canSubmit ? (
              `Enter All Products (${filledCount}/${totalProducts})`
            ) : isEditMode ? (
              `Update ${type === 'MORNING' ? 'Morning' : 'Evening'} Count`
            ) : isAdmin ? (
              `Save ${type === 'MORNING' ? 'Morning' : 'Evening'} Count`
            ) : (
              `Submit ${type === 'MORNING' ? 'Morning' : 'Evening'} Count for Review`
            )}
          </Button>

          {totalProducts > 0 && filledCount < totalProducts && (
            <p className="text-xs text-yellow-500 text-center mt-2">
              ⚠️ Please enter values for all products before saving
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
